import { initializeApp } from "firebase/app";
import {
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  setDoc,
  orderBy,
  query,
  where,
  updateDoc,
  DocumentReference,
  deleteDoc,
  limit,
} from "firebase/firestore";
import { getDatabase, ref as databaseRef, set } from "firebase/database";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { getAnalytics } from "firebase/analytics";
import { HorseAd } from "../classes/HorseAd";
import User from "../classes/User";
import UserProgress from "../classes/UserProgress";
import { NotificationType } from "../components/ui/NotificationsPopup";
import { GroupMember } from "../classes/GroupMemberProgress";

const app = initializeApp({
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGINNG_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MESUREMENT_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
});

// Ensure `window` is properly typed
declare global {
  interface Window {
    FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string;
  }
}

// ✅ Dynamically Set App Check Debug Mode
if (process.env.REACT_APP_DEBUG === "true") {
  window.FIREBASE_APPCHECK_DEBUG_TOKEN = "2884A77A-78BF-4BBF-A8A3-28C10632DE9E"; // Enables debug mode
}

initializeAppCheck(app, {
  provider: new ReCaptchaV3Provider("6LcmlusqAAAAAANH485mk2lQ-ineDXc08yvDz5-q"),
  isTokenAutoRefreshEnabled: true,
});

const analytics = getAnalytics(app);

export const auth = getAuth(app);
export const firestore = getFirestore(app);
export const database = getDatabase(app);
export const storage = getStorage(app);

export const ROOT_NODE: string = "HorseCollection";
export const USERS_ROOT: string = "Users";
export const GROUPS_ROOT: string = "Groups";

export const writeDataToFirestore = async (path: string, data: any) => {
  try {
    const docRef = collection(firestore, path);
    const docId = docRef.id;
    data["id"] = docId;

    return await addDoc(docRef, data);
  } catch (error) {
    console.log("Error while writing data to firestore: ", error);
    return error;
  }
};

// Function to fetch the latest user progress
export const fetchLatestUserProgress = async (memberId: string) => {
  try {
    const progressRef = collection(
      firestore,
      `${USERS_ROOT}/${memberId}/UserProgress`
    );
    const q = query(progressRef, orderBy("created_at", "desc"), limit(1));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      return querySnapshot.docs[0].data() as UserProgress;
    }

    return null;
  } catch (error) {
    console.error(
      `Error fetching latest progress for user ${memberId}:`,
      error
    );
    return null;
  }
};

export const fetchUserName = async (
  memberId: string
): Promise<GroupMember | null> => {
  try {
    const userDocRef = doc(firestore, USERS_ROOT, memberId);
    const userDocSnap = await getDoc(userDocRef);

    if (userDocSnap.exists()) {
      return {
        userName: userDocSnap.data().userName || "Anonymous",
        profilePicURL: userDocSnap.data().profilePicUrl || "",
      };
    } else {
      console.warn(`User document not found for ID: ${memberId}`);
      return null;
    }
  } catch (error) {
    console.error(`Error fetching user name for ID ${memberId}:`, error);
    return null;
  }
};

export const fetchNotifications = async (
  userId: string
): Promise<NotificationType[]> => {
  try {
    const invitesRef = collection(firestore, "GroupInvites");
    const q = query(
      invitesRef,
      where("receiverId", "==", userId),
      where("status", "==", "pending")
    );
    const querySnapshot = await getDocs(q);

    const inviteData: NotificationType[] = await Promise.all(
      querySnapshot.docs.map(async (docSnap) => {
        const data = docSnap.data();
        const senderRef = doc(firestore, "Users", data.senderId);
        const senderSnap = await getDoc(senderRef);
        const senderName = senderSnap.exists()
          ? senderSnap.data().userName
          : "Unknown";

        return {
          id: docSnap.id,
          senderName,
          groupId: data.groupId, // Ensure groupId is included
          groupName: data?.groupName,
        };
      })
    );

    return inviteData;
  } catch (error) {
    console.error("Error fetching notifications:", error);
    return [];
  }
};

export const acceptGroupInvite = async (
  inviteId: string,
  userId: string
): Promise<void> => {
  try {
    const inviteRef = doc(firestore, "GroupInvites", inviteId);
    const inviteSnap = await getDoc(inviteRef);

    if (!inviteSnap.exists()) throw new Error("Invite not found");

    const inviteData = inviteSnap.data();
    const groupRef = doc(firestore, "Groups", inviteData.groupId);
    const groupSnap = await getDoc(groupRef);

    if (groupSnap.exists()) {
      const groupData = groupSnap.data();
      const updatedMembers = [...(groupData.members || []), userId];

      await updateDoc(groupRef, { members: updatedMembers });
      await updateDoc(inviteRef, { status: "accepted" });
    }
  } catch (error) {
    console.error("Error accepting invite:", error);
    throw error;
  }
};

export const dismissGroupInvite = async (inviteId: string): Promise<void> => {
  try {
    const inviteRef = doc(firestore, "GroupInvites", inviteId);
    await deleteDoc(inviteRef);
  } catch (error) {
    console.error("Error dismissing invite:", error);
    throw error;
  }
};

export const fetchUserGroups = async (userId: string) => {
  if (!userId) return [];

  try {
    const q = query(
      collection(firestore, GROUPS_ROOT),
      where("members", "array-contains", userId)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error fetching groups:", error);
    throw error;
  }
};

export const writeGroupDataToFirestore = async (path: string, data: any) => {
  try {
    const docRef = await addDoc(collection(firestore, path), data);

    // 🔹 Add the document ID inside the document itself
    await setDoc(
      doc(firestore, path, docRef.id),
      { id: docRef.id },
      { merge: true }
    );

    return docRef; // ✅ Now it has an `id`
  } catch (error) {
    console.error("Error while writing data to Firestore:", error);
    throw error;
  }
};

export const getGroupDetailsFromFirestore = async (groupId: string) => {
  try {
    const groupRef = doc(firestore, GROUPS_ROOT, groupId);
    const groupSnap = await getDoc(groupRef);

    if (!groupSnap.exists()) {
      throw new Error("Group not found");
    }

    // Define the expected type for groupData
    const groupData: {
      id: string;
      name?: string;
      createdAt?: string;
      members?: string[];
      memberDetails?: GroupMember[];
    } = { id: groupSnap.id, ...groupSnap.data() };

    // Fetch member details
    if (groupData.members && Array.isArray(groupData.members)) {
      const memberDetails = await Promise.all(
        groupData.members.map(async (memberId: string) => {
          return await fetchUserName(memberId);
        })
      );

      // Store the transformed data separately
      groupData.memberDetails = memberDetails.filter(
        (member): member is GroupMember => member !== null
      );
    }

    return groupData;
  } catch (error) {
    console.error("Error fetching group details:", error);
    throw error;
  }
};

export const inviteUserToGroup = async (
  groupId: string,
  senderId: string,
  inviteCode: string,
  groupName: string,
  senderName: string
) => {
  try {
    // Step 1: Find user by uniqueCode
    const usersRef = collection(firestore, USERS_ROOT);
    const q = query(usersRef, where("email", "==", inviteCode));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error("User not found");
    }

    const receiverId = querySnapshot.docs[0].id;

    // Step 2: Check if the user is already in the group
    const groupRef = collection(firestore, GROUPS_ROOT);
    const groupSnapshot = await getDocs(
      query(groupRef, where("id", "==", groupId))
    );

    if (groupSnapshot.empty) {
      throw new Error("Group not found");
    }

    const groupData = groupSnapshot.docs[0].data();
    if (groupData.members.includes(receiverId)) {
      throw new Error("User is already in the group");
    }

    // Step 3: Store the invitation in GroupInvites
    const docRef = collection(firestore, "GroupInvites");
    const docData = {
      id: docRef.id,
      groupId: groupId,
      senderId: senderId,
      receiverId: receiverId,
      groupName: groupName,
      senderName: senderName,
      status: "pending",
    };

    await addDoc(docRef, docData);

    return "Invitation sent!";
  } catch (error) {
    console.error("Error sending invite:", error);
    throw error;
  }
};

export const signUp = (email: string, password: string) => {
  return createUserWithEmailAndPassword(auth, email, password);
};

export const doSignInWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    // Check if the user already exists in Firestore
    const userRef = doc(firestore, USERS_ROOT, user.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      // New user → Save to Firestore
      await setDoc(userRef, {
        id: user.uid,
        uid: user.uid,
        userName: user.displayName,
        userId: user.email,
        email: user.email,
        mobileNumber: "",
        profilePicUrl: user.photoURL,
        userType: "",
        createdAt: new Date().getMilliseconds(), // Store signup timestamp
      });
      console.log("New user created:", user);
    } else {
      console.log("Existing user signed in:", user);
    }

    return user;
  } catch (error) {
    console.error("Google Sign-In Error:", error);
    throw error;
  }
};

export const signOutUser = async () => {
  try {
    await auth.signOut();
    console.log("User signed out");
  } catch (error) {
    console.error("Sign out error:", error);
  }
};

export const writeDataToFirestoreWithCustomID = async (
  path: string,
  id: string,
  data: any
) => {
  try {
    const docRef = doc(firestore, path, id);
    const docId = docRef.id;
    data["id"] = docId;

    return await setDoc(docRef, data);
  } catch (error) {
    console.log("Error while writing data to firestore 1: ", error);
    throw error;
  }
};

export const writeNewProgressToFB = async (data: UserProgress) => {
  try {
    const path = "Users/" + data.user_id + "/UserProgress";
    data["status"] = 1;
    return await writeDataToFirestoreWithCustomID(path, data.id, data);
  } catch (error) {
    console.log("Error while writing data to firestore 3: ", error);
    throw error;
  }
};

export const fetchUserProgressFromDB = async (userId: string) => {
  try {
    const path = `Users/${userId}/UserProgress`;
    const userProgressRef = collection(firestore, path);

    // Fetch documents ordered by `created_time` in descending order (newest first)
    const q = query(userProgressRef, orderBy("created_at", "desc"));

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.log("Error while fetching data from Firestore: ", error);
    throw error;
  }
};

export const fetchUserProgress = async (userId: string, progressId: string) => {
  try {
    const path = `Users/${userId}/UserProgress/${progressId}`;
    const userProgressRef = doc(firestore, path);

    const snapshot = await getDoc(userProgressRef);
    return snapshot.data();
  } catch (error) {
    console.log("Error while fetching data from Firestore: ", error);
    throw error;
  }
};

export const writeWelcomeEmailTrigger = async (userData: User) => {
  try {
    if (!userData.id || !userData.userId || !userData.userName) {
      throw new Error(
        "Invalid user data. Ensure id, email, and name are provided."
      );
    }

    // Reference to the Realtime Database path
    const dbRef = databaseRef(
      database,
      `/mailtriggers/welcomemail/${userData.id}`
    );

    // Write the data
    await set(dbRef, {
      email: userData.userId,
      name: userData.userName,
    });

    console.log("Welcome email trigger written successfully!");
  } catch (error) {
    console.error("Error writing welcome email trigger: ", error);
    throw error;
  }
};

export const writeNewCustomer = async (id: string, data: any) => {
  try {
    return await writeDataToFirestoreWithCustomID("Users", id, data);
  } catch (error) {
    console.log("Error while writing data to firestore 2: ", error);
    throw error;
  }
};

export const writeNewHorseAd = async (id: string, data: any) => {
  try {
    return await writeDataToFirestoreWithCustomID(ROOT_NODE, id, data);
  } catch (error) {
    console.log("Error while writing data to firestore: ", error);
    throw error;
  }
};

export const writeNewTicketToFB = async (
  projectId: string,
  id: string,
  data: any
) => {
  try {
    const path = ROOT_NODE + "/" + projectId + "/Tickets/";
    data["id"] = id;

    return writeDataToFirestoreWithCustomID(path, id, data);
  } catch (error) {
    console.log("Error while writing ticket data to firestore: ", error);
    throw error;
  }
};

export const writeNewReleaseToFB = async (
  projectId: string,
  id: string,
  data: any
) => {
  try {
    const path = ROOT_NODE + "/" + projectId + "/Releases/";
    data["id"] = id;

    return writeDataToFirestoreWithCustomID(path, id, data);
  } catch (error) {
    console.log("Error while writing ticket data to firestore: ", error);
    throw error;
  }
};

export const getDocsFromFirestore = async (path: string) => {
  try {
    const docsRef = collection(firestore, path);

    return await getDocs(docsRef);
  } catch (error) {
    console.log("Error while reading data from firestore: ", error);
    throw error;
  }
};

export const getDocsFromFirestoreWithSort = async (
  path: string,
  parm: string,
  order: "desc" | "asc"
) => {
  try {
    const docsRef = collection(firestore, path);
    const q = query(docsRef, orderBy(parm, order));

    return await getDocs(q);
  } catch (error) {
    // console.log("Error while reading data from firestore: ", error);
    throw error;
  }
};

export const getDocsFromFirestoreWhereEquals = async (
  path: string,
  parm: string,
  value: string
) => {
  try {
    const docsRef = collection(firestore, path);
    const q = query(docsRef, where(parm, "==", value));

    return await getDocs(q);
  } catch (error) {
    // console.log("Error while reading data from firestore: ", error);
    throw error;
  }
};

export const getMyHorsesFromFirestoreWhereEquals = async (userId: string) => {
  const parm = "selleruserId";
  try {
    const docsRef = collection(firestore, ROOT_NODE);
    const q = query(
      docsRef,
      where(parm, "==", userId),
      where("status", "in", [0, 1, 3, 4])
    );

    return await getDocs(q);
  } catch (error) {
    // console.log("Error while reading data from firestore: ", error);
    throw error;
  }
};

export const deleteTheHorseAd = async (ad: HorseAd) => {
  try {
    const newDocRef = doc(firestore, ROOT_NODE + "/" + ad.id);

    return await updateDoc(newDocRef, { status: 2 });
  } catch (error) {
    throw error;
  }
};

export const updateTheHorseAd = async (updatedAd: HorseAd) => {
  try {
    const newDocRef = doc(firestore, ROOT_NODE + "/" + updatedAd.id);

    return await updateDoc(newDocRef, { ...updatedAd });
  } catch (error) {
    throw error;
  }
};

export const getSignleDocFromFirestore = async (path: string) => {
  try {
    const docsRef = doc(firestore, path);

    return await getDoc(docsRef);
  } catch (error) {
    console.log("Error while reading signle doc from firestore: ", error);
    throw error;
  }
};

export const getDocsFromFirestoreWithSortWithoutLimit = async (
  parm: string,
  order: "desc" | "asc"
) => {
  try {
    const docsRef = collection(firestore, ROOT_NODE);
    const q = query(docsRef, orderBy(parm, order));

    return await getDocs(q);
  } catch (error) {
    // console.log("Error while reading data from firestore: ", error);
    throw error;
  }
};

export const getDocsFromFirestoreWithCondition = async (
  parm: string,
  order: "desc" | "asc"
) => {
  try {
    const docsRef = collection(firestore, ROOT_NODE);
    const q = query(
      docsRef,
      where("status", "==", 1) // Filter for documents with status equal to 1
      // orderBy(parm, order)
    );

    return await getDocs(q);
  } catch (error) {
    throw error;
  }
};

export const getUserData = async (sellerId: string) => {
  try {
    const userRootNode = "Users";
    const docsRef = doc(firestore, `${userRootNode}/${sellerId}`);

    return await getDoc(docsRef);
  } catch (error) {
    console.log("Error while reading signle doc from firestore: ", error);
    throw error;
  }
};

export const uploadTicketMedia = async (userId: string, mediaFiles: File[]) => {
  let downloadableUrls = [];

  // Upload files to Firebase Storage
  const uploadTasks = mediaFiles.map((file) => {
    const timestamp = Date.now();
    const storageRef = ref(
      storage,
      `ImageFolder/image/${userId}/${timestamp}-${file.name}`
    );
    const uploadTask = uploadBytesResumable(storageRef, file);
    return uploadTask;
  });

  try {
    // Get download URLs and save to Firestore after all uploads are complete
    const snapshots = await Promise.all(uploadTasks);

    const urls = await Promise.all(
      snapshots.map((snapshot) => getDownloadURL(snapshot.ref))
    );

    downloadableUrls.push(...urls);

    // Return the downloadableUrls array
    return downloadableUrls;
  } catch (error) {
    console.log("Error uploading media:", error);
    // Handle the error, you might want to throw it or handle it in a specific way
    throw error;
  }
};

export const uploadHorseMedia = async (userId: string, mediaFiles: File[]) => {
  let downloadableUrls = [];

  // Upload files to Firebase Storage
  const uploadTasks = mediaFiles.map((file) => {
    const timestamp = Date.now();
    const storageRef = ref(
      storage,
      `ImageFolder/image/${userId}/${timestamp}-${file.name}`
    );
    const uploadTask = uploadBytesResumable(storageRef, file);
    return uploadTask;
  });

  try {
    // Get download URLs and save to Firestore after all uploads are complete
    const snapshots = await Promise.all(uploadTasks);

    const urls = await Promise.all(
      snapshots.map((snapshot) => getDownloadURL(snapshot.ref))
    );

    downloadableUrls.push(...urls);

    // Return the downloadableUrls array
    return downloadableUrls;
  } catch (error) {
    console.log("Error uploading media:", error);
    // Handle the error, you might want to throw it or handle it in a specific way
    throw error;
  }
};

export const uploadReleaseMedia = async (
  userId: string,
  mediaFiles: File[]
) => {
  let downloadableUrls = [];

  // Upload files to Firebase Storage
  const uploadTasks = mediaFiles.map((file) => {
    const timestamp = Date.now();
    const storageRef = ref(
      storage,
      `ProfilePictures/${userId}/${timestamp}-${file.name}`
    );
    const uploadTask = uploadBytesResumable(storageRef, file);
    return uploadTask;
  });

  try {
    // Get download URLs and save to Firestore after all uploads are complete
    const snapshots = await Promise.all(uploadTasks);

    const urls = await Promise.all(
      snapshots.map((snapshot) => getDownloadURL(snapshot.ref))
    );

    downloadableUrls.push(...urls);

    // Return the downloadableUrls array
    return downloadableUrls;
  } catch (error) {
    console.error("Error uploading media:", error);
    // Handle the error, you might want to throw it or handle it in a specific way
    throw error;
  }
};

export default auth;
